import axios from 'axios';
import { Endpoints } from './endpoints';
import { IFields, IFilter } from '../../util/url';
import { url } from './util';
import {
  IApplicationOptions, IChannel, IDestination, IOccupation, IIndustry, IPolicyApplication, IPostcodeAddress,
  IPostcodeLookup, TripType, ICustomer, ITrackingLink,
} from './models';
import { application, channels, destinations, occupations, industries,
  customer, trackingLink, content } from './transformers';
import moment = require('moment');

const CancelToken = axios.CancelToken;
let cancel = null;

export function listContents(filters?: IFilter[], fields: IFields = null) {
  return axios
    .get(url(Endpoints.CONTENT_LIST, { filters , fields, includes: [ 'template', 'files' ]}))
    .then((response) => {
      return content.fromJsonApi(response.data.data, response.data.included);
    });
}

export function createPolicyApplication(policyApplication: IPolicyApplication,
                                        channelId: string,
                                        brokerId: string,
                                        productCode: string,
                                        options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculatePremium) {
    extraParams.calculate = 'true';
  }

  return axios
    .post(
      url(
        Endpoints.POLICY_APPLICATIONS,
        {
          params: policyApplication.id ? { id: policyApplication.id, ...extraParams } : {},
          includes: options.calculatePremium ? ['premiums.scheme', 'customer'] : [],
        },
      ),
      { data: application.toJsonApi(policyApplication, channelId, brokerId, productCode) },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function updatePolicyApplication(policyApplication: IPolicyApplication,
                                        options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  if (cancel) {
    cancel();
  }

  const extraParams = {
    calculate: 'false',
  };

  if (options.calculatePremium) {
    extraParams.calculate = 'true';
  }

  const request = options.patch ? axios.patch : axios.put;

  return request(
      url(
        Endpoints.POLICY_APPLICATION,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculatePremium ? ['premiums.scheme', 'customer'] : [],
        },
      ),
      { data: application.toJsonApi(policyApplication) },
      { headers: { 'Content-Type': 'application/vnd.api+json' },
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        }),
      },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function getAggregatorPolicyApplication(id: string): Promise<IPolicyApplication> {
  return axios
    .get(
      url(
        Endpoints.AGGREGATOR_POLICY_APPLICATION,
        {
          params: { id },
          includes: ['premiums.scheme', 'customer', 'channel.products', 'channel.product_documents'],
        },
      ),
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function saveQuote(policyApplication: IPolicyApplication,
                          channelId: string,
                          options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculatePremium) {
    extraParams.calculate = 'true';
  }

  return axios
    .post(
      url(
        Endpoints.POLICY_APPLICATIONS,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculatePremium ? ['premiums.scheme', 'customer'] : [],
        },
      ),
      { data: application.toJsonApi(policyApplication, channelId) },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function issuePolicyApplication(policyApplication: IPolicyApplication) {
  const reuseAddress = policyApplication.reuseCustomerAddress;

  const billingDetails = {
    name: policyApplication.payment && policyApplication.payment.billing ?
      policyApplication.payment.billing.name : '',
    email: policyApplication.payment && policyApplication.payment.billing ?
      policyApplication.payment.billing.email : '',
    phone: policyApplication.payment && policyApplication.payment.billing ?
      policyApplication.payment.billing.phone : '',
    address: {
      postcode: policyApplication.payment && policyApplication.payment.billing ?
        policyApplication.payment.billing.address.postcode : '',
      line1: policyApplication.payment && policyApplication.payment.billing ?
        policyApplication.payment.billing.address.line1 : '',
      line2: policyApplication.payment && policyApplication.payment.billing ?
        policyApplication.payment.billing.address.line2 : '',
      town: policyApplication.payment && policyApplication.payment.billing ?
        policyApplication.payment.billing.address.town : '',
      county: policyApplication.payment && policyApplication.payment.billing ?
        policyApplication.payment.billing.address.county : '',
    },
  };
  return axios
    .post(
      url(Endpoints.POLICY_APPLICATION_ISSUE, { params: { id: policyApplication.id } }),
      {
        meta: {
          sms_send: policyApplication.sendSms,
          purchase_declaration: policyApplication.purchaseDeclaration,
          billing: reuseAddress ? {} : billingDetails,
        },
        data: {
          relationships: {
            scheme: {
              data: {
                id: policyApplication.schemeId,
              },
            },
          },
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function getTrackingLink(id: string): Promise<ITrackingLink> {
  return axios
    .get(url(Endpoints.TRACKING_LINKS, { params: { id } }))
    .then((response) => {
      return trackingLink.fromJsonApi(response.data.data, response.data.included);
    });
}

export function checkCampaignCode(code, chanelId: string) {
  return axios
    .get(
      url(Endpoints.CAMPAIGN_CODE, { params: { code, channel: chanelId } }),
    )
    .then((response) => {
      return response.data;
    });
}

export function listDestinations(): Promise<IDestination[]> {
  // Absurdly high limit to get pass limitations in pagination
  return axios
    .get(url(Endpoints.DESTINATIONS, { limit: 10000 }))
    .then((response) => {
      return destinations.fromJsonApi(response.data.data);
    });
}

export function listOccupations(): Promise<IOccupation[]> {
  // Absurdly high limit to get pass limitations in pagination
  return axios
    .get(url(Endpoints.OCCUPATIONS, { limit: 10000 }))
    .then((response) => {
      return occupations.fromJsonApi(response.data.data);
    });
}

export function listIndustries(): Promise<IIndustry[]> {
  // Absurdly high limit to get pass limitations in pagination
  return axios
    .get(url(Endpoints.INDUSTRIES, { limit: 10000 }))
    .then((response) => {
      return industries.fromJsonApi(response.data.data);
    });
}

export function getChannel(key: string): Promise<IChannel> {
  return axios
    .post(url(Endpoints.CHANNEL, { params: { key }, includes: [ 'products', 'product_documents' ] }))
    .then((response) => {
      return channels.fromJsonApi(response.data.data, response.data.included);
    });
}

// export function getCustomer(key: string): Promise<ICustomer> {
//   return axios
//     .post(url(Endpoints.CUSTOMER_PORTAL, { params: { key }, includes: [ 'customer' ] }))
//     .then((response) => {
//       return customer.fromJsonApi(response.data.data, response.data.included);
//     });
// }

export function startScreeningCega(id?: string): Promise<string> {
  return axios
    .post(
      url(Endpoints.SCREENING_START_CEGA),
      id ? { meta: { id } } : null,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data.meta;
    });
}

export function finishScreeningCega(id: string): Promise<any> {
  return axios
    .post(
      url(Endpoints.SCREENING_FINISH_CEGA),
      { meta: { id } },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function lookupPostcode(postcode: string): Promise<IPostcodeLookup[]> {
  return axios
    .get(
      url(Endpoints.POSTCODE_LOOKUP, { params: { postcode: encodeURIComponent(postcode) } }),
    )
    .then((response) => {
      return response.data.data.map((lookup) => ({
        id: lookup.id,
        line: lookup.attributes.name,
      }));
    });
}

export function getPostcode(id: string): Promise<IPostcodeAddress> {
  return axios
    .get(
      url(Endpoints.POSTCODE_GET, { params: { id } }),
    )
    .then((response) => {
      return response.data.data.attributes;
    });
}

export function getPaymentUrl(transactionId: string) {
  return axios.defaults.baseURL + '/' + url(Endpoints.TRANSACTION_PAY, {
    params: {
      id: transactionId,
    },
  });
}

export function getCustomer() {
  return axios
    .get(
      url(Endpoints.CUSTOMER, {
        includes: [
          'policies.documents',
          'quotes.channel',
          'policies.premiums',
          'quotes.documents',
          // 'renewals.documents',
        ] }),
    )
    .then((response) => {
      return customer.fromJsonApi(response.data.data, response.data.included);
    });
}

export function updateCustomer(customerValues: ICustomer) {
  return axios
    .patch(
      url(Endpoints.CUSTOMER, {
        includes: ['policies.documents', 'policies.premiums',
          'quotes.documents', 'quotes.premiums'] }),
      { data: customer.toJsonApi(customerValues) },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return customer.fromJsonApi(response.data.data, response.data.included);
    });
}

export function passwordReminder(request) {
  return axios
    .post(
      url(Endpoints.FORGOTTEN_PASSWORD),
      request,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function accountCreator(request) {
  return axios
    .post(
      url(Endpoints.FORGOTTEN_PASSWORD),
      request,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

// noinspection TsLint
export function resetPassword(token, { password, passwordConfirmation, email, dob }) {
  return axios
    .post(
      url(Endpoints.PASSWORD_RESET),
      {
        token,
        password,
        password_confirmation: passwordConfirmation,
        dob : moment(dob, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        email,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function showDestinations(tripType: TripType): boolean {
  return tripType === 'single';
}

export function showRegions(tripType: TripType): boolean {
  return true;
}

export function documentsPoll(documentsPollToken: string) {
  return axios
    .post(
      url(Endpoints.POLICY_DOCUMENTS_POLL),
      { documentsPollToken },
      { headers: { 'Content-Type': 'application/vnd.api+json' }},
    )
    .then((response) => {
      return response.data;
    });
}

export function referralQuote(id, referrals) {
  return axios
    .post(
      url(Endpoints.REFERRAL_QUOTE, { params: { id }}),
      { meta: { referrals }},
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/data/api/api.ts